import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Command,
  Gamepad2,
  Home,
  Inbox,
  LayoutDashboard,
  Lightbulb,
  LogIn,
  MessageCircle,
  Plus,
  Settings,
  Sparkles,
  Ticket,
  TrendingUp,
  Trophy,
  User,
  UserPlus,
  Users,
  Gift,
} from "lucide-react";
import {
  logoutAppUser,
  selectAppIsAuthenticated,
  selectAppUser,
} from "../redux/appAuthSlice";
import { fetchActivitySummary } from "../redux/activitySlice";
import {
  CREATE_BET_HOUSE_PATH,
  withCreateBetHouseParam,
} from "../utils/createBetHouseHelpers";
import { formatBadgeCount } from "../utils/formatBadgeCount";
import AccountProfileMenu, { getAccountInitial } from "./ui/AccountProfileMenu";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";
import { useAccountProfilePortal } from "../hooks/useAccountProfilePortal";
import { HOME_PATH, isProductEnabled } from "../config/products";

const WS_RAIL_DEFAULT = 248;
const WS_RAIL_MIN = 68;
const WS_RAIL_MAX = 380;
const WS_RAIL_COMPACT = 120;
const WS_MOBILE_MQ = "(max-width: 1024px)";

const isMac = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);

const GROUPS = [
  {
    id: "play",
    label: "Play surfaces",
    items: [
      { to: "/app/bethouse", label: "House", Icon: Home, hint: "The floor", product: "house" },
      { to: "/app/p2p", label: "P2P", Icon: Users, hint: "Head to head", product: "p2p" },
      { to: "/app/bond-predict", label: "BondPredict", Icon: TrendingUp, hint: "YES / NO markets", product: "predict" },
      { to: "/app/bond-play", label: "Play", Icon: Gamepad2, hint: "Wheel · Spin · Draw · Coin · Dice · Numbers", product: "play" },
    ],
  },
  {
    id: "community",
    label: "Community",
    items: [
      { to: "/app/predictions", label: "Tips", Icon: Lightbulb, hint: "Community calls", product: "tips" },
      { to: "/app/leaderboard", label: "Leaderboard", Icon: Trophy, hint: "Rankings · scores", product: "leaderboard" },
      { to: "/app/referrals", label: "Referrals", Icon: Gift, hint: "Invite & earn" },
    ],
  },
  {
    id: "pulse",
    label: "Pulse",
    items: [
      { to: "/app/general-chat", label: "BondChat", Icon: MessageCircle, hint: "Live chat & fixtures", badgeKey: "chat", product: "chat" },
      {
        to: "/app/activity",
        label: "Bond Pulse",
        Icon: Activity,
        hint: "Your account story",
        badgeKey: "activity",
        pulseWhenBadge: true,
      },
    ],
  },
  {
    id: "workspace",
    label: "Your workspace",
    requiresAuth: true,
    items: [
      { to: "/app/dashboard", label: "Dashboard", Icon: LayoutDashboard, hint: "Overview & snapshot" },
      { to: "/app/joined-bets", label: "Joined bets", Icon: Ticket, hint: "Your bet tickets", product: "house" },
      { to: "/app/requests", label: "Requests", Icon: Inbox, hint: "P2P invites" },
      { to: "/app/bethouse/manage", label: "Operator console", Icon: Settings, hint: "Manage your house", product: "house" },
      { to: "/app/profile", label: "Profile", Icon: User, hint: "Account & settings" },
    ],
  },
];

const getInitial = getAccountInitial;

const SectionRow = ({
  to,
  label,
  Icon,
  hint,
  badgeValue,
  collapsed,
  pulseWhenBadge,
}) => {
  const hasLivePulse = pulseWhenBadge && badgeValue > 0;

  return (
  <NavLink
    to={to}
    end={to === "/app/bethouse"}
    className={({ isActive }) =>
      `wsnav-row ${isActive ? "wsnav-row-active" : ""}${hasLivePulse ? " wsnav-row-has-pulse" : ""}`
    }
    title={collapsed ? `${label}${hint ? ` — ${hint}` : ""}` : undefined}
  >
    <span className="wsnav-row-indicator" aria-hidden />
    <span
      className={`wsnav-row-icon${hasLivePulse ? " wsnav-row-icon-pulse" : ""}`}
    >
      <Icon size={16} strokeWidth={2.2} />
      {hasLivePulse ? (
        <span className="wsnav-row-pulse-dot wsnav-pip wsnav-pip-live" aria-hidden />
      ) : null}
    </span>
    <span className="wsnav-row-label">
      <span className="wsnav-row-label-main">{label}</span>
      <span className="wsnav-row-label-hint">{hint}</span>
    </span>
    {badgeValue > 0 ? (
      <span
        className={`wsnav-row-badge${hasLivePulse ? " wsnav-row-badge-live" : ""}`}
        aria-label={`${badgeValue} unread`}
      >
        {formatBadgeCount(badgeValue)}
      </span>
    ) : null}
  </NavLink>
  );
};

const AppWorkspaceNav = ({ onOpenPalette }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const user = useSelector(selectAppUser);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const chatUnread = useSelector((s) => s.generalChat?.unreadCount || 0);
  const activityUnread = useSelector((s) => s.activities?.unreadCount || 0);
  const socketConnected = useSelector(
    (s) => s.generalChat?.connected || false
  );

  const [accountOpen, setAccountOpen] = useState(false);
  const accountWrapRef = useRef(null);
  const accountBtnRef = useRef(null);
  const accountMenuRef = useRef(null);

  const { size: railWidth, setClampedSize: setRailWidth, reset: resetRailWidth } =
    usePersistedPanelSize("bb-ws-rail-w", {
      defaultSize: WS_RAIL_DEFAULT,
      minSize: WS_RAIL_MIN,
      maxSize: WS_RAIL_MAX,
    });
  const isCompact = railWidth <= WS_RAIL_COMPACT;

  const accountMenuStyle = useAccountProfilePortal(
    accountOpen,
    accountBtnRef,
    { mobileMq: WS_MOBILE_MQ },
    [railWidth]
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    document.body.style.setProperty("--ws-rail-w", `${railWidth}px`);
    document.body.classList.toggle("app-workspace-collapsed", isCompact);
  }, [railWidth, isCompact]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (
      window.localStorage.getItem("bb-ws-collapsed") === "1" &&
      !window.localStorage.getItem("bb-ws-rail-w")
    ) {
      setRailWidth(WS_RAIL_MIN);
      window.localStorage.removeItem("bb-ws-collapsed");
    }
  }, [setRailWidth]);

  const onRailResizePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      const startX = event.clientX;
      const startSize = railWidth;
      document.body.classList.add("ws-is-resizing");

      const onMove = (ev) => {
        setRailWidth(startSize + (ev.clientX - startX));
      };
      const onUp = () => {
        document.body.classList.remove("ws-is-resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [railWidth, setRailWidth]
  );

  const toggleRailWidth = useCallback(() => {
    if (isCompact) resetRailWidth();
    else setRailWidth(WS_RAIL_MIN);
  }, [isCompact, resetRailWidth, setRailWidth]);

  useEffect(() => {
    if (isAuthenticated && user?._id) dispatch(fetchActivitySummary());
  }, [isAuthenticated, user?._id, dispatch]);

  useEffect(() => {
    setAccountOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!accountOpen) return;
    const close = (e) => {
      const inWrap = accountWrapRef.current?.contains(e.target);
      const inMenu = accountMenuRef.current?.contains(e.target);
      if (!inWrap && !inMenu) setAccountOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [accountOpen]);

  const handleSignOut = useCallback(() => {
    setAccountOpen(false);
    dispatch(logoutAppUser());
    navigate(HOME_PATH);
  }, [dispatch, navigate]);

  const badges = useMemo(
    () => ({ chat: chatUnread, activity: activityUnread }),
    [chatUnread, activityUnread]
  );

  const resolveBadgeValue = (item) =>
    item.badgeKey ? badges[item.badgeKey] || 0 : 0;

  const createTo = isAuthenticated
    ? withCreateBetHouseParam(pathname, search)
    : "/app/login";
  const createState = isAuthenticated
    ? undefined
    : { from: CREATE_BET_HOUSE_PATH };

  const kbdHint = isMac() ? "⌘K" : "Ctrl K";

  return (
    <aside
      className={`wsnav${isCompact ? " wsnav-compact" : ""}`}
      aria-label="Workspace navigation"
    >
      <header className="wsnav-head">
        <Link to={HOME_PATH} className="wsnav-brand" aria-label="BetBond home">
          <span className="wsnav-brand-mark">
            <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden>
              <defs>
                <linearGradient id="wsnav-brand-arc" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#64B5F6" />
                  <stop offset="100%" stopColor="#1E88E5" />
                </linearGradient>
              </defs>
              <path
                d="M 6 18 Q 16 4 26 18"
                stroke="url(#wsnav-brand-arc)"
                strokeWidth="2.8"
                strokeLinecap="round"
                fill="none"
              />
              <circle cx="6" cy="18" r="3" fill="#fff" />
              <circle cx="26" cy="18" r="3" fill="#fff" />
              <circle cx="16" cy="11" r="1.6" fill="#64B5F6" />
            </svg>
          </span>
          <span className="wsnav-brand-copy">
            <span className="wsnav-brand-name">
              <span>Bet</span>
              <span className="wsnav-brand-accent">Bond</span>
            </span>
            <span className="wsnav-brand-tag">
              <span
                className={`wsnav-pip ${
                  socketConnected ? "wsnav-pip-live" : "wsnav-pip-idle"
                }`}
                aria-hidden
              />
              {socketConnected ? "Online" : "Syncing"}
            </span>
          </span>
        </Link>
        <button
          type="button"
          className="wsnav-collapse"
          onClick={toggleRailWidth}
          aria-label={isCompact ? "Expand sidebar" : "Collapse sidebar"}
          title={isCompact ? "Expand" : "Collapse"}
        >
          {isCompact ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </header>

      <button
        type="button"
        onClick={onOpenPalette}
        className="wsnav-cmdk"
        aria-label={`Open command palette (${kbdHint})`}
      >
        <Command size={13} strokeWidth={2.2} />
        <span className="wsnav-cmdk-copy">
          <span className="wsnav-cmdk-label">Jump to…</span>
          <span className="wsnav-cmdk-hint">Search pages & actions</span>
        </span>
        <span className="wsnav-cmdk-kbd">{kbdHint}</span>
      </button>

      <nav className="wsnav-scroll" aria-label="Sections">
        {GROUPS.map((group) => {
          if (group.requiresAuth && !isAuthenticated) return null;
          const items = group.items.filter((item) => isProductEnabled(item.product));
          if (!items.length) return null;
          return (
            <div className="wsnav-group" key={group.id}>
              <p className="wsnav-group-label">{group.label}</p>
              <div className="wsnav-group-list">
                {items.map((item) => (
                  <SectionRow
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    Icon={item.Icon}
                    hint={item.hint}
                    badgeValue={resolveBadgeValue(item)}
                    collapsed={isCompact}
                    pulseWhenBadge={item.pulseWhenBadge}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {isProductEnabled("house") ? (
      <div className="wsnav-create-wrap">
        <p className="wsnav-create-eyebrow">Launch a new floor</p>
        <Link
          to={createTo}
          state={createState}
          className="wsnav-create"
          title="Create House"
        >
          <span className="wsnav-create-icon" aria-hidden>
            <Plus size={14} strokeWidth={2.6} />
          </span>
          <span className="wsnav-create-label">Create House</span>
        </Link>
      </div>
      ) : (
      <div className="wsnav-create-wrap">
        <p className="wsnav-create-eyebrow">Start a challenge</p>
        <Link
          to={isAuthenticated ? "/app/p2p/create" : "/app/login"}
          state={isAuthenticated ? undefined : { from: "/app/p2p/create" }}
          className="wsnav-create"
          title="Create P2P"
        >
          <span className="wsnav-create-icon" aria-hidden>
            <Plus size={14} strokeWidth={2.6} />
          </span>
          <span className="wsnav-create-label">Create P2P</span>
        </Link>
      </div>
      )}

      <div className="wsnav-foot">
        {isAuthenticated ? (
          <div className="wsnav-account-wrap" ref={accountWrapRef}>
            <button
              type="button"
              ref={accountBtnRef}
              className={`wsnav-account ${accountOpen ? "wsnav-account-open" : ""}`}
              onClick={() => setAccountOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={accountOpen}
              aria-label={`Account · ${user?.name || ""}`}
            >
              <span className="wsnav-account-avatar">{getInitial(user)}</span>
              <span className="wsnav-account-copy">
                <span className="wsnav-account-name">
                  {user?.name || "Member"}
                </span>
                <span className="wsnav-account-meta">
                  {user?.email || "Active"}
                </span>
              </span>
              <ChevronUp
                size={13}
                className={`wsnav-account-chev ${
                  accountOpen ? "wsnav-account-chev-open" : ""
                }`}
                aria-hidden
              />
            </button>
            {accountOpen &&
              accountMenuStyle &&
              createPortal(
                <AccountProfileMenu
                  ref={accountMenuRef}
                  user={user}
                  isAuthenticated={isAuthenticated}
                  onSignOut={handleSignOut}
                  onClose={() => setAccountOpen(false)}
                  className="app-profile-menu-portal"
                  style={accountMenuStyle}
                />,
                document.body
              )}
          </div>
        ) : (
          <div className="wsnav-guest">
            <p className="wsnav-guest-copy">
              <Sparkles size={12} aria-hidden />
              Sign in to challenge friends, track tickets and join the lounge.
            </p>
            <div className="wsnav-guest-actions">
              <Link to="/app/login" className="wsnav-guest-btn">
                <LogIn size={13} /> Log in
              </Link>
              <Link
                to="/app/signup"
                className="wsnav-guest-btn wsnav-guest-btn-primary"
              >
                <UserPlus size={13} /> Sign up
              </Link>
            </div>
          </div>
        )}

        <div className="wsnav-foot-meta">
          <span className="wsnav-foot-meta-item">
            <Activity size={10} aria-hidden /> Bond desk
          </span>
        </div>
      </div>

      <div
        className="wsnav-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={railWidth}
        aria-valuemin={WS_RAIL_MIN}
        aria-valuemax={WS_RAIL_MAX}
        aria-label="Resize navigation"
        title="Drag to resize · double-click to reset"
        onPointerDown={onRailResizePointerDown}
        onDoubleClick={resetRailWidth}
      />
    </aside>
  );
};

export default AppWorkspaceNav;
